import { FC, ReactNode } from "react";
import { NavLink, useLocation } from "react-router";

import classNames from "classnames";

import { BaseProps } from "../../BaseProps";
import { createComponentStyleHook } from "../../tokens";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { isPathWithinBasePath } from "../../utilities/isPathWithinBasePath";
import { Tooltip } from "../Tooltip";

import legacyStyles from "./legacy/styles.module.scss";
import tokenStyles from "./styles.module.css";

/** The `Item` properties. */
export interface ItemProps extends BaseProps {
  /** The path to navigate to. */
  readonly path?: string;

  /** The optional switch to disable exact path matching. */
  readonly nonExactMatch?: boolean;

  /**
   * The optional base path that keeps the item highlighted while the current route is that base path or one of its
   * descendants. Use this only when the highlighted routes differ from `path`, such as a `/` item that must stay
   * highlighted across `/employee/:id`. To keep an item highlighted on its own child routes, use `nonExactMatch`.
   */
  readonly activeBasePath?: string;

  /** The item tooltip. */
  readonly tooltip?: ReactNode;

  /** The component children. */
  readonly children?: ReactNode;
}

const useStyles = createComponentStyleHook({
  legacyStyles,
  tokenStyles,
  componentTokens: "sidebar_nav",
});

/** The in-context sidebar navigation component. */
export const Item: FC<ItemProps> = (props) => {
  const { children, path, nonExactMatch = false, activeBasePath, tooltip } = props;
  const { mode, styles, StyleModeBoundary } = useStyles(props);

  const location = useLocation();

  const isBasePathActive = !!activeBasePath && isPathWithinBasePath(location.pathname, activeBasePath);

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [styles.Item, "item"],
  });

  if (path) {
    let isSubNavigation = false;
    let isSubActive = false;

    if (path.startsWith("#")) {
      isSubNavigation = true;
      const url = new URL(`http://test/${path}`);
      isSubActive = url.hash === location.hash;
    }

    const hashAriaCurrent = isSubNavigation && isSubActive ? "page" : undefined;

    return (
      <StyleModeBoundary mode={mode}>
        <Tooltip content={tooltip}>
          {(ref) => (
            <NavLink
              {...baseHtmlProps}
              ref={ref}
              to={path}
              end={!nonExactMatch}
              className={({ isActive: navIsActive }) => {
                const isActive = isSubNavigation ? navIsActive && isSubActive : navIsActive || isBasePathActive;
                return classNames(baseHtmlProps.className, { active: isActive });
              }}
              aria-current={isSubNavigation ? hashAriaCurrent : undefined}
            >
              {children}
            </NavLink>
          )}
        </Tooltip>
      </StyleModeBoundary>
    );
  }

  return (
    <Tooltip content={tooltip}>
      {(ref) => (
        <div {...baseHtmlProps} ref={ref}>
          {children}
        </div>
      )}
    </Tooltip>
  );
};
